import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { SalonHours } from "@/components/settings/SalonHours";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReferralSettingsPanel } from "@/components/common/ReferralSettingsPanel";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Sun, Moon, Monitor, AlertTriangle, Download, Upload, RefreshCw, CheckCircle2, MessageCircle, Send, XCircle, Loader2, Trash2, Plus, Zap } from "lucide-react";

export default function Impostazioni() {
  const { user, theme, setTheme } = useAuth();
  const [settings, setSettings] = useState(null);
  const [booking, setBooking] = useState(null);
  const [comms, setComms] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [commStats, setCommStats] = useState(null);
  const [email, setEmail] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testEvent, setTestEvent] = useState("booking_confirmed");
  const [testChannel, setTestChannel] = useState("email");
  const [testBusy, setTestBusy] = useState(false);
  const [services, setServices] = useState([]);
  const [commPeriod, setCommPeriod] = useState("month");
  const [showWaAdvanced, setShowWaAdvanced] = useState(false);
  const [acDialogOpen, setAcDialogOpen] = useState(false);
  const [acBusy, setAcBusy] = useState(false);
  const enableAutoConsent = async () => {
    setAcBusy(true);
    try {
      const { data } = await api.post("/settings/auto-consent/enable");
      setSettings((s) => ({ ...s, auto_consent: { enabled: true, activated_at: data.activated_at, activated_by: data.activated_by } }));
      setAcDialogOpen(false);
      toast.success(`Gestione automatica attivata · ${data.updated} clienti aggiornati`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore attivazione"); }
    finally { setAcBusy(false); }
  };
  const disableAutoConsent = async () => {
    try {
      await api.post("/settings/auto-consent/disable");
      setSettings((s) => ({ ...s, auto_consent: { ...(s.auto_consent || {}), enabled: false } }));
      toast.success("Gestione automatica disattivata (i consensi già registrati restano)");
    } catch (e) { toast.error("Errore"); }
  };
  // Automazioni WhatsApp aggiuntive (oltre alle 4 core): create dall'Admin, persistite in DB
  const [manualCards, setManualCards] = useState(null); // null = non inizializzato
  useEffect(() => {
    if (manualCards === null && settings && settings.whatsapp_templates) {
      const extra = Object.keys(settings.whatsapp_templates)
        .filter((k) => !FIXED_WA_EVENTS.includes(k) && (settings.whatsapp_templates[k] || {}).name);
      setManualCards(extra.map((ev) => ({ id: `cfg-${ev}`, event: ev })));
    }
  }, [settings, manualCards]);
  const addAutomationCard = () => setManualCards((c) => [...(c || []), { id: `new-${Date.now()}`, event: "" }]);
  const removeAutomationCard = async (id, ev) => {
    setManualCards((c) => (c || []).filter((x) => x.id !== id));
    if (ev && (settings?.whatsapp_templates || {})[ev]) {
      const t = { ...(settings.whatsapp_templates || {}) }; delete t[ev];
      const ns = { ...settings, whatsapp_templates: t };
      setSettings(ns);
      try { await api.put("/settings", ns); toast.message("Automazione rimossa"); } catch (e) { /* noop */ }
    }
  };
  const pickAutomationEvent = (id, ev) => setManualCards((c) => (c || []).map((x) => (x.id === id ? { ...x, event: ev } : x)));
  const canWrite = user && (user.role === "admin" || user.role === "manager");

  const loadEmail = () => api.get("/email/overview").then((r) => setEmail(r.data)).catch(() => {});

  const loadComms = (period) => {
    const p = period || commPeriod;
    api.get("/communications").then((r) => setComms(r.data)).catch(() => {});
    api.get(`/comms/analytics?period=${p}`).then((r) => setCommStats(r.data)).catch(() => {});
  };
  const loadIntegrations = () => api.get("/integrations").then((r) => setIntegrations(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data.settings));
    api.get("/booking-config").then((r) => setBooking(r.data)).catch(() => {});
    loadComms(); loadIntegrations();
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    if (canWrite) loadEmail();
    const onRefresh = () => loadIntegrations();
    window.addEventListener("integrations:refresh", onRefresh);
    return () => window.removeEventListener("integrations:refresh", onRefresh);
  }, []);

  const saveEmailConfig = async () => {
    try { await api.put("/settings", settings); toast.success("Configurazione email salvata"); loadEmail(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const sendTestEmail = async () => {
    if (!testTo.includes("@")) { toast.error("Inserisci un indirizzo valido"); return; }
    try { const { data } = await api.post("/email/test-send", { to: testTo }); toast.success(`Prova ${data.sandbox ? "(sandbox) " : ""}inviata: ${data.status}`); loadEmail(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
  };
  const runBirthdays = async () => {
    try { const { data } = await api.post("/automations/birthday-greetings/run"); toast.success(`Auguri in coda: ${data.queued}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveBooking = async () => {
    try { await api.put("/booking-config", booking); toast.success("Regole booking salvate"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleIntegration = async (p, patch) => {
    try { await api.put(`/integrations/${p.provider}`, patch); toast.success("Integrazione aggiornata"); loadIntegrations(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const testIntegration = async (p) => {
    try { const { data } = await api.post(`/integrations/${p.provider}/test`); data.ok ? toast.success(`Test ${p.provider}: ${data.mode}`) : toast.error(data.error || "Test fallito"); loadIntegrations(); }
    catch (e) { toast.error("Errore test"); }
  };
  // Credenziali provider (salvate lato server nel config dell'integrazione, mai esposte)
  const [cred, setCred] = useState({});
  const CRED_LABELS = { api_key: "API Key", token: "Access Token", phone_id: "Phone Number ID", waba_id: "WhatsApp Business Account ID (WABA)", webhook_secret: "Webhook Secret", app_id: "App ID" };
  const saveCredentials = async (p) => {
    const config = {};
    (p.credential_fields || []).forEach((f) => {
      const v = cred[`${p.provider}:${f}`];
      if (v && v.trim()) config[f] = v.trim();
    });
    if (Object.keys(config).length === 0) { toast.error("Inserisci almeno una credenziale"); return; }
    try {
      await api.put(`/integrations/${p.provider}`, { config });
      toast.success("Credenziali salvate in modo sicuro");
      setCred((c) => { const n = { ...c }; (p.credential_fields || []).forEach((f) => delete n[`${p.provider}:${f}`]); return n; });
      loadIntegrations();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };
  // WhatsApp: configurazione template approvati (necessari per invii marketing reali)
  const WA_EVENTS = [
    { key: "birthday", label: "Auguri compleanno", hint: "Parametri suggeriti: nome, salone" },
    { key: "reminder", label: "Promemoria appuntamento", hint: "Parametri suggeriti: nome, data, ora" },
    { key: "booking_confirmed", label: "Conferma prenotazione", hint: "Parametri suggeriti: nome, data, ora" },
  ];
  const setWaTemplate = (ev, patch) => {
    setSettings((s) => {
      const tpls = { ...(s?.whatsapp_templates || {}) };
      tpls[ev] = { ...(tpls[ev] || {}), ...patch };
      return { ...s, whatsapp_templates: tpls };
    });
  };
  const saveWhatsappConfig = async () => {
    try { await api.put("/settings", settings); toast.success("Template WhatsApp salvati"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const retryComm = async (c) => {
    try { await api.post(`/communications/${c.id}/retry`); toast.success("Reinvio in coda"); loadComms(); }
    catch { toast.error("Errore"); }
  };
  const STATUS_COLOR = { connected: "success", test_mode: "warning", disconnected: "muted", incomplete: "danger" };

  const save = async () => {
    try { await api.put("/settings", settings); toast.success("Impostazioni salvate"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Stato REALE dei provider (per banner dinamico e label). "connected" = produzione attiva.
  const waHealth = integrations.find((p) => p.provider === "whatsapp");
  const waLive = waHealth?.status === "connected";
  const liveProviders = integrations.filter((p) => p.status === "connected");
  const sandboxProviders = integrations.filter((p) => p.status !== "connected");

  return (
    <div>
      <PageHeader title="Impostazioni" subtitle="Configurazione salone e preferenze" />
      <Tabs defaultValue="aspetto">
        <TabsList>
          <TabsTrigger value="aspetto" data-testid="settings-tab-appearance">Aspetto</TabsTrigger>
          <TabsTrigger value="salone" data-testid="settings-tab-salon">Salone</TabsTrigger>
          <TabsTrigger value="orari" data-testid="settings-tab-hours">Orari</TabsTrigger>
          <TabsTrigger value="booking" data-testid="settings-tab-booking">Booking & Portale</TabsTrigger>
          <TabsTrigger value="integrazioni" data-testid="settings-tab-integrations">Integrazioni</TabsTrigger>
          <TabsTrigger value="comunicazioni" data-testid="settings-tab-comms">Comunicazioni</TabsTrigger>
          {(user?.role === "admin" || user?.role === "manager") && <TabsTrigger value="privacy" data-testid="settings-tab-privacy">Privacy e consensi</TabsTrigger>}
          {(user?.role === "admin" || user?.role === "manager") && <TabsTrigger value="referral" data-testid="settings-tab-referral">Referral</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="email" data-testid="settings-tab-email">Email</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="permessi" data-testid="settings-tab-rbac">Ruoli & Permessi</TabsTrigger>}
          {user?.role === "admin" && <TabsTrigger value="manutenzione" data-testid="settings-tab-maintenance">Manutenzione</TabsTrigger>}
        </TabsList>

        <TabsContent value="aspetto" className="mt-4">
          <Card className="p-5 max-w-md">
            <h3 className="font-heading text-xl font-semibold mb-1">Tema</h3>
            <p className="text-sm text-muted-foreground mb-4">La preferenza viene salvata sul tuo profilo.</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setTheme("light")} data-testid="settings-theme-light" className={`rounded-lg border p-4 flex flex-col items-center gap-2 ${theme === "light" ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border"}`}><Sun className="h-6 w-6" /><span className="text-sm font-medium">Chiara</span></button>
              <button onClick={() => setTheme("dark")} data-testid="settings-theme-dark" className={`rounded-lg border p-4 flex flex-col items-center gap-2 ${theme === "dark" ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border"}`}><Moon className="h-6 w-6" /><span className="text-sm font-medium">Scura</span></button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="salone" className="mt-4">
          {settings && (
            <Card className="p-5 max-w-2xl space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Valuta</Label><Input value={settings.currency} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>IVA (%)</Label><Input type="number" value={settings.vat_percent} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, vat_percent: +e.target.value })} data-testid="settings-vat" /></div>
                <div className="space-y-1.5"><Label>Caparra predefinita (%)</Label><Input type="number" value={settings.deposit_default_pct} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, deposit_default_pct: +e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Preavviso cancellazione (ore)</Label><Input type="number" value={settings.cancellation_hours} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, cancellation_hours: +e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Metodi di pagamento</Label><Input value={(settings.payment_methods||[]).join(", ")} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, payment_methods: e.target.value.split(",").map((s) => s.trim()) })} /></div>
              {canWrite ? <Button onClick={save} data-testid="settings-save">Salva impostazioni</Button> : <p className="text-xs text-muted-foreground">Solo Admin/Manager possono modificare.</p>}
            </Card>
          )}
        </TabsContent>
        <TabsContent value="orari" className="mt-4">
          <SalonHours />
        </TabsContent>

        <TabsContent value="booking" className="mt-4">
          {booking && (
            <Card className="p-5 max-w-2xl space-y-4">
              <h3 className="font-heading text-xl font-semibold">Regole prenotazione online</h3>
              <div className="flex items-center justify-between"><div><Label>Scelta del professionista</Label><p className="text-xs text-muted-foreground">La cliente può scegliere l'operatore</p></div><Switch checked={!!booking.allow_choose_professional} disabled={!canWrite} onCheckedChange={(v) => setBooking({ ...booking, allow_choose_professional: v })} data-testid="booking-choose-pro" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Visibilità prezzi</Label><Select value={booking.price_visibility} onValueChange={(v) => setBooking({ ...booking, price_visibility: v })} disabled={!canWrite}><SelectTrigger data-testid="booking-price-visibility"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="show">Mostra prezzi</SelectItem><SelectItem value="from_only">Solo "da €"</SelectItem><SelectItem value="hide">Nascondi</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Passo slot (min)</Label><Input type="number" value={booking.slot_step_min} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, slot_step_min: +e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Anticipo minimo (ore)</Label><Input type="number" value={booking.min_advance_hours} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, min_advance_hours: +e.target.value })} data-testid="booking-min-advance" /></div>
                <div className="space-y-1.5"><Label>Anticipo massimo (giorni)</Label><Input type="number" value={booking.max_advance_days} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, max_advance_days: +e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Finestra cancellazione (ore)</Label><Input type="number" value={booking.cancellation_window_hours} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, cancellation_window_hours: +e.target.value })} data-testid="booking-cancel-window" /></div>
                <div className="space-y-1.5"><Label>Orari (apertura - chiusura)</Label><div className="flex gap-2"><Input type="number" value={booking.open_hour} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, open_hour: +e.target.value })} /><Input type="number" value={booking.close_hour} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, close_hour: +e.target.value })} /></div></div>
                <div className="space-y-1.5"><Label>Caparra</Label><Select value={booking.deposit_mode} onValueChange={(v) => setBooking({ ...booking, deposit_mode: v })} disabled={!canWrite}><SelectTrigger data-testid="booking-deposit-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Nessuna</SelectItem><SelectItem value="fixed">Importo fisso</SelectItem><SelectItem value="percent">Percentuale</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Valore caparra</Label><Input type="number" value={booking.deposit_value} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, deposit_value: +e.target.value })} /></div>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-4"><div><Label>Lista d'attesa</Label><p className="text-xs text-muted-foreground">Attiva la lista d'attesa quando non ci sono slot</p></div><Switch checked={!!booking.waitlist_enabled} disabled={!canWrite} onCheckedChange={(v) => setBooking({ ...booking, waitlist_enabled: v })} data-testid="booking-waitlist" /></div>
              <div className="space-y-1.5"><Label>Link recensione</Label><Input value={booking.review_link || ""} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, review_link: e.target.value })} /></div>
              {canWrite ? <Button onClick={saveBooking} data-testid="booking-save">Salva regole booking</Button> : <p className="text-xs text-muted-foreground">Solo Admin/Manager possono modificare.</p>}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="integrazioni" className="mt-4">
          {liveProviders.length === 0 ? (
            <div className="mb-3 rounded-lg border border-[hsl(var(--hi-status-warning)/0.4)] bg-[hsl(var(--hi-status-warning)/0.1)] p-3 text-sm flex items-start gap-2" data-testid="integrations-mode-banner">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--hi-status-warning))] shrink-0" />
              <div>Modalità <b>Test/Sandbox</b>: nessun messaggio o pagamento viene inviato a clienti reali. Per andare in produzione: inserisci le credenziali, imposta <b>Attiva</b> = ON e <b>Test mode</b> = OFF per il provider desiderato.</div>
            </div>
          ) : (
            <div className="mb-3 rounded-lg border border-[hsl(var(--hi-status-success)/0.4)] bg-[hsl(var(--hi-status-success)/0.1)] p-3 text-sm flex items-start gap-2" data-testid="integrations-mode-banner">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--hi-status-success))] shrink-0" />
              <div>
                <b>Produzione attiva</b> per: {liveProviders.map((p) => p.label).join(", ")} — gli invii/pagamenti sono REALI.
                {sandboxProviders.length > 0 && <span className="text-muted-foreground"> · Ancora in sandbox: {sandboxProviders.map((p) => p.label).join(", ")}.</span>}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {integrations.map((p) => (
              <Card key={p.provider} className="p-4" data-testid={`integration-${p.provider}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{p.label}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(--hi-status-${STATUS_COLOR[p.status] || "muted"}))` }} />
                      <span className="text-xs capitalize text-muted-foreground">{p.status.replace("_", " ")}</span>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{p.credentials_present ? "credenziali OK" : "nessuna chiave"}</Badge>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">Env richieste: {p.required_env.join(", ")}</div>
                {canWrite && (p.credential_fields || []).length > 0 && (
                  <div className="mt-3 space-y-2 rounded-md border border-border/60 bg-muted/30 p-2.5" data-testid={`integration-creds-${p.provider}`}>
                    <div className="text-[11px] font-medium text-muted-foreground">Credenziali (salvate solo lato server)</div>
                    {(p.credential_fields || []).map((f) => (
                      <div key={f} className="space-y-1">
                        <Label className="text-[11px]">{CRED_LABELS[f] || f}{p.config_masked?.[f] ? <span className="text-muted-foreground font-normal"> · attuale: {p.config_masked[f]}</span> : null}</Label>
                        <Input type="password" autoComplete="off" value={cred[`${p.provider}:${f}`] || ""}
                          onChange={(e) => setCred((c) => ({ ...c, [`${p.provider}:${f}`]: e.target.value }))}
                          placeholder={p.config_masked?.[f] ? "•••• (lascia vuoto per mantenere)" : "Inserisci valore"}
                          className="h-8 text-xs" data-testid={`integration-cred-${p.provider}-${f}`} />
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="h-7 mt-1" onClick={() => saveCredentials(p)} data-testid={`integration-cred-save-${p.provider}`}>Salva credenziali</Button>
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2 text-sm"><span>Attiva</span><Switch checked={p.enabled} disabled={!canWrite} onCheckedChange={(v) => toggleIntegration(p, { enabled: v })} data-testid={`integration-toggle-${p.provider}`} /></div>
                  <div className="flex items-center gap-2 text-sm"><span>Test mode</span><Switch checked={p.test_mode} disabled={!canWrite} onCheckedChange={(v) => toggleIntegration(p, { test_mode: v })} /></div>
                  <Button variant="outline" size="sm" onClick={() => testIntegration(p)} disabled={!canWrite} data-testid={`integration-test-${p.provider}`}>Test</Button>
                </div>
              </Card>
            ))}
          </div>

          {canWrite && <WhatsAppTest defaultCc={settings?.whatsapp_default_country_code} />}

          {user?.role === "admin" && (
            <Card className="p-5 mt-4" data-testid="whatsapp-templates-card">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-lg font-semibold">WhatsApp — Template approvati (Meta Cloud API)</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Per inviare messaggi WhatsApp reali di tipo marketing (es. auguri) <b>fuori dalla finestra di 24h</b> è necessario un <b>template approvato</b> da Meta.
                Inserisci qui il nome esatto del template, la lingua e l'ordine dei parametri del corpo (le variabili {"{{1}}, {{2}}"}...). Lascia vuoto per inviare testo libero (valido solo entro 24h).
              </p>
              <div className="space-y-4">
                <div className="max-w-[220px]">
                  <Label className="text-xs">Prefisso internazionale predefinito</Label>
                  <Input value={settings?.whatsapp_default_country_code || ""} disabled={!canWrite}
                    onChange={(e) => setSettings({ ...settings, whatsapp_default_country_code: e.target.value.replace(/\D/g, "") })}
                    placeholder="39" data-testid="wa-default-cc" />
                </div>
                {WA_EVENTS.map((ev) => {
                  const t = (settings?.whatsapp_templates || {})[ev.key] || {};
                  return (
                    <div key={ev.key} className="rounded-lg border border-border p-3 space-y-2" data-testid={`wa-template-${ev.key}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{ev.label}</span>
                        <span className="text-[11px] text-muted-foreground">{ev.hint}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="space-y-1"><Label className="text-xs">Nome template</Label>
                          <Input value={t.name || ""} disabled={!canWrite} onChange={(e) => setWaTemplate(ev.key, { name: e.target.value })} placeholder="es. auguri_compleanno" data-testid={`wa-tpl-name-${ev.key}`} /></div>
                        <div className="space-y-1"><Label className="text-xs">Lingua</Label>
                          <Input value={t.language || ""} disabled={!canWrite} onChange={(e) => setWaTemplate(ev.key, { language: e.target.value })} placeholder="it" data-testid={`wa-tpl-lang-${ev.key}`} /></div>
                        <div className="space-y-1"><Label className="text-xs">Parametri corpo (in ordine)</Label>
                          <Input value={(t.param_keys || []).join(", ")} disabled={!canWrite}
                            onChange={(e) => setWaTemplate(ev.key, { param_keys: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                            placeholder="nome, salone" data-testid={`wa-tpl-params-${ev.key}`} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {canWrite && <Button className="mt-4" onClick={saveWhatsappConfig} data-testid="wa-templates-save">Salva template WhatsApp</Button>}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="privacy" className="mt-4">
          <Card className="p-5 space-y-4 max-w-2xl">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[hsl(var(--hi-gold))] mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label>Gestione automatica consensi</Label>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-lg">Registra i consensi come già acquisiti esternamente: attiva in blocco i consensi per i clienti esistenti e li applica automaticamente ai nuovi, senza selezionarli a ogni cliente o appuntamento.</p>
                  </div>
                  <Switch
                    checked={!!settings?.auto_consent?.enabled}
                    disabled={!canWrite}
                    onCheckedChange={(v) => { if (v) setAcDialogOpen(true); else disableAutoConsent(); }}
                    data-testid="auto-consent-toggle"
                  />
                </div>
                {settings?.auto_consent?.enabled && (
                  <div className="mt-3 text-xs rounded-md border border-[hsl(var(--hi-gold)/0.35)] bg-[hsl(var(--hi-gold)/0.06)] p-3" data-testid="auto-consent-status">
                    <CheckCircle2 className="h-3.5 w-3.5 inline mr-1 text-[hsl(var(--hi-gold))]" />
                    Attiva · consensi acquisiti esternamente e attivati da <strong>{settings.auto_consent.activated_by || "amministratore"}</strong>
                    {settings.auto_consent.activated_at ? ` il ${new Date(settings.auto_consent.activated_at).toLocaleString("it-IT")}` : ""}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-3">Puoi sempre disattivare uno o più consensi dalla singola scheda cliente: la scelta individuale ha priorità e non verrà riattivata automaticamente.</p>
              </div>
            </div>
          </Card>

          <Dialog open={acDialogOpen} onOpenChange={setAcDialogOpen}>
            <DialogContent data-testid="auto-consent-dialog">
              <DialogHeader><DialogTitle className="font-heading text-xl">Conferma gestione automatica consensi</DialogTitle></DialogHeader>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Confermando dichiari che i <strong>consensi dei clienti sono già stati acquisiti esternamente</strong> al gestionale (moduli cartacei/firme in salone). IdentityOS li registrerà e attiverà in blocco per tutti i clienti già presenti e li applicherà automaticamente ai nuovi. La scelta manuale su una singola scheda avrà sempre priorità.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAcDialogOpen(false)} data-testid="auto-consent-abort">Annulla</Button>
                <Button onClick={enableAutoConsent} disabled={acBusy} data-testid="auto-consent-confirm">{acBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confermo e attivo</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="comunicazioni" className="mt-4">
          {commStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[["Email inviate", commStats.emails_sent ?? 0], ["WhatsApp inviati", commStats.whatsapp_sent ?? 0], ["Click recensione", commStats.review_clicks ?? 0], ["Msg post-pagamento", commStats.post_payment_sent ?? 0]].map(([l, v]) => (
                <Card key={l} className="p-3" data-testid={`comm-stat-${l}`}><div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div><div className="font-heading text-2xl font-semibold">{v}</div></Card>
              ))}
            </div>
          )}
          {commStats && (commStats.by_event || []).length > 0 && (
            <Card className="p-5 mb-4 max-w-2xl" data-testid="comm-report-card">
              <div className="mb-3 flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-heading text-lg font-semibold">Report per tipo di messaggio</h3>
                  <p className="text-sm text-muted-foreground">Totale, inviati e non riusciti per ogni automazione.</p>
                </div>
                <select className="h-9 rounded-md border border-border bg-background px-3 text-sm" value={commPeriod} onChange={(e) => { setCommPeriod(e.target.value); loadComms(e.target.value); }} data-testid="comm-period-select">
                  <option value="today">Oggi</option>
                  <option value="week">Questa settimana</option>
                  <option value="month">Questo mese</option>
                  <option value="quarter">Questo trimestre</option>
                  <option value="year">Quest'anno</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 font-medium">Tipo</th><th className="py-2 font-medium text-right">Totale</th><th className="py-2 font-medium text-right">Inviati</th><th className="py-2 font-medium text-right">Non riusciti</th>
                  </tr></thead>
                  <tbody>
                    {commStats.by_event.map((r) => (
                      <tr key={r.event} className="border-b border-border/60" data-testid={`comm-report-row-${r.event}`}>
                        <td className="py-2">{r.label}</td>
                        <td className="py-2 text-right tabular">{r.total}</td>
                        <td className="py-2 text-right tabular text-[hsl(var(--hi-gold))]">{r.sent}</td>
                        <td className={`py-2 text-right tabular ${r.failed ? "text-destructive" : "text-muted-foreground"}`}>{r.failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {settings && (
            <Card className="p-5 mb-4 max-w-2xl space-y-4" data-testid="post-payment-card">
              <div>
                <h3 className="font-heading text-lg font-semibold">Messaggi automatici dopo il pagamento</h3>
                <p className="text-sm text-muted-foreground">Alla chiusura del conto invia al cliente un riepilogo con servizi, importo, stylist e il pulsante per lasciare una recensione.</p>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div><Label>Attiva invio post-pagamento</Label><p className="text-xs text-muted-foreground">Master on/off</p></div>
                <Switch checked={settings.post_payment_comms_enabled !== false} disabled={!canWrite} onCheckedChange={(v) => setSettings({ ...settings, post_payment_comms_enabled: v })} data-testid="pp-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <Label>Email</Label>
                  <Switch checked={settings.post_payment_email_enabled !== false} disabled={!canWrite} onCheckedChange={(v) => setSettings({ ...settings, post_payment_email_enabled: v })} data-testid="pp-email" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div><Label>WhatsApp</Label><p className="text-[10px] text-muted-foreground">{waLive ? "produzione (invio reale)" : "simulato (sandbox)"}</p></div>
                  <Switch checked={settings.post_payment_whatsapp_enabled !== false} disabled={!canWrite} onCheckedChange={(v) => setSettings({ ...settings, post_payment_whatsapp_enabled: v })} data-testid="pp-whatsapp" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Link recensione Google</Label>
                <Input value={booking?.review_link || ""} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, review_link: e.target.value })} placeholder="https://g.page/r/.../review" data-testid="pp-review-link" />
                <p className="text-[11px] text-muted-foreground">Usato nel pulsante "Lascia una recensione". I click vengono tracciati nelle statistiche.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Link Google Maps (promemoria)</Label>
                <Input value={settings.maps_url || ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, maps_url: e.target.value })} placeholder="https://maps.google.com/?q=Hair+Identity+Rende" data-testid="pp-maps-url" />
                <p className="text-[11px] text-muted-foreground">Aggiunto automaticamente come "Come arrivare" nel promemoria delle 3 ore prima. Variabile disponibile nei template: <code>{"{{mappa}}"}</code></p>
              </div>
              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="text-sm font-medium">Personalizza testi</div>
                <p className="text-[11px] text-muted-foreground">Variabili disponibili: <code>{"{cliente}"}</code> <code>{"{stylist}"}</code> <code>{"{salone}"}</code> <code>{"{servizi}"}</code> <code>{"{importo}"}</code> <code>{"{metodo}"}</code> <code>{"{link_recensione}"}</code></p>
                <div className="space-y-1.5">
                  <Label>Oggetto email</Label>
                  <Input value={settings.post_payment_email_subject ?? ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, post_payment_email_subject: e.target.value })} placeholder="Grazie da {salone}! Lascia una recensione" data-testid="pp-email-subject" />
                </div>
                <div className="space-y-1.5">
                  <Label>Testo email (introduzione)</Label>
                  <Textarea rows={3} value={settings.post_payment_email_body ?? ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, post_payment_email_body: e.target.value })} placeholder="Grazie {cliente}! Grazie per aver scelto {salone}. Ecco il riepilogo del tuo servizio con {stylist}." data-testid="pp-email-body" />
                  <p className="text-[10px] text-muted-foreground">Il riepilogo servizi/importo e il pulsante recensione vengono aggiunti automaticamente sotto questo testo.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Testo WhatsApp</Label>
                  <Textarea rows={3} value={settings.post_payment_whatsapp_body ?? ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, post_payment_whatsapp_body: e.target.value })} placeholder="Grazie {cliente}! Ecco il riepilogo: {servizi} - Totale {importo}. Lascia una recensione: {link_recensione}" data-testid="pp-whatsapp-body" />
                  <p className="text-[10px] text-muted-foreground">Nota: WhatsApp fuori dalla finestra di 24h richiede un <b>template approvato</b> (sotto). Il testo libero è solo un promemoria interno.</p>
                </div>
              </div>

              {/* Stato semplificato delle automazioni WhatsApp (uso quotidiano) */}
              <WhatsAppAutomationsStatus />

              {/* Impostazioni avanzate (solo Admin): template mapping + diagnostica */}
              {canWrite && (
                <div className="rounded-md border border-dashed border-border">
                  <button type="button" onClick={() => setShowWaAdvanced((v) => !v)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/40 transition-colors duration-150" data-testid="wa-advanced-toggle">
                    <span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Impostazioni avanzate WhatsApp (Admin)</span>
                    <span className="text-xs text-muted-foreground">{showWaAdvanced ? "Nascondi" : "Mostra"}</span>
                  </button>
                  {showWaAdvanced && (
                    <div className="p-3 pt-0 space-y-3">
                      <PostVisitTemplate event="booking_confirmed" label="Conferma appuntamento" varKeys={VAR_APPT} settings={settings} setSettings={setSettings} canWrite={canWrite} waLive={waLive} />
                      <PostVisitTemplate event="reminder" label="Promemoria 24h" varKeys={VAR_APPT} settings={settings} setSettings={setSettings} canWrite={canWrite} waLive={waLive} />
                      <PostVisitTemplate event="reminder_3h" label="Promemoria 3h" varKeys={VAR_APPT} settings={settings} setSettings={setSettings} canWrite={canWrite} waLive={waLive} />
                      <PostVisitTemplate event="post_payment" label="Ringraziamento post-visita" varKeys={PV_VAR_KEYS} settings={settings} setSettings={setSettings} canWrite={canWrite} waLive={waLive} />

                      {/* Automazioni aggiuntive create dall'Admin (persistite nel DB) */}
                      {(manualCards || []).map((card) => (
                        <PostVisitTemplate key={card.id} event={card.event}
                          pickable={!card.event}
                          eventOptions={AUTOMATION_EVENTS.filter((o) => !FIXED_WA_EVENTS.includes(o.event) && !(manualCards || []).some((mc) => mc.id !== card.id && mc.event === o.event))}
                          onPicked={(ev) => pickAutomationEvent(card.id, ev)}
                          onRemove={() => removeAutomationCard(card.id, card.event)}
                          settings={settings} setSettings={setSettings} canWrite={canWrite} waLive={waLive} />
                      ))}

                      {canWrite && (
                        <Button type="button" variant="outline" size="sm" className="w-full border-dashed" onClick={addAutomationCard} data-testid="wa-add-automation">
                          <Plus className="h-4 w-4 mr-1" /> Aggiungi automazione
                        </Button>
                      )}

                      <WhatsAppDiagnostics />
                    </div>
                  )}
                </div>
              )}
              {canWrite && <div className="flex gap-2"><Button onClick={async () => { await save(); await saveBooking(); loadComms(); }} data-testid="pp-save">Salva</Button></div>}
            </Card>
          )}
          {booking && (
            <Card className="p-5 mb-4 max-w-2xl space-y-4" data-testid="recall-card">
              <div>
                <h3 className="font-heading text-lg font-semibold">Richiami post-servizio (winback)</h3>
                <p className="text-sm text-muted-foreground">Invita automaticamente le clienti a riprenotare dopo un servizio (es. colore). Il 2º richiamo parte solo se non hanno già prenotato. Nessun richiamo se esiste un appuntamento futuro per la stessa categoria.</p>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <div><Label>Attiva richiami</Label><p className="text-xs text-muted-foreground">Master on/off · invio simulato (sandbox)</p></div>
                <Switch checked={booking.recall?.enabled !== false} disabled={!canWrite} onCheckedChange={(v) => setBooking({ ...booking, recall: { ...(booking.recall || {}), enabled: v } })} data-testid="recall-enabled" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>1º richiamo (giorni dopo)</Label><Input type="number" value={booking.recall?.first_days ?? 25} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, recall: { ...(booking.recall || {}), first_days: Number(e.target.value) || 0 } })} data-testid="recall-first-days" /></div>
                <div className="space-y-1.5"><Label>2º richiamo (giorni dopo)</Label><Input type="number" value={booking.recall?.second_days ?? 33} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, recall: { ...(booking.recall || {}), second_days: Number(e.target.value) || 0 } })} data-testid="recall-second-days" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Categorie servizio (separate da virgola)</Label>
                <Input value={(booking.recall?.categories || []).join(", ")} disabled={!canWrite} onChange={(e) => setBooking({ ...booking, recall: { ...(booking.recall || {}), categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} placeholder="Colore, Schiariture" data-testid="recall-categories" />
                <p className="text-[11px] text-muted-foreground">Solo gli appuntamenti completati e pagati di queste categorie generano un richiamo.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-md border border-border p-3"><Label>Canale Email</Label>
                  <Switch checked={(booking.recall?.channels || ["email"]).includes("email")} disabled={!canWrite} onCheckedChange={(v) => { const ch = new Set(booking.recall?.channels || ["email"]); v ? ch.add("email") : ch.delete("email"); setBooking({ ...booking, recall: { ...(booking.recall || {}), channels: [...ch] } }); }} data-testid="recall-ch-email" /></div>
                <div className="flex items-center justify-between rounded-md border border-border p-3"><div><Label>Canale WhatsApp</Label><p className="text-[10px] text-muted-foreground">simulato (sandbox)</p></div>
                  <Switch checked={(booking.recall?.channels || []).includes("whatsapp")} disabled={!canWrite} onCheckedChange={(v) => { const ch = new Set(booking.recall?.channels || ["email"]); v ? ch.add("whatsapp") : ch.delete("whatsapp"); setBooking({ ...booking, recall: { ...(booking.recall || {}), channels: [...ch] } }); }} data-testid="recall-ch-whatsapp" /></div>
              </div>
              {canWrite && <div className="rounded-md border border-border p-3 space-y-3" data-testid="recall-per-service">
                <div>
                  <Label>Richiami per singolo servizio (opzionale)</Label>
                  <p className="text-[11px] text-muted-foreground">Imposta giorni diversi per servizi specifici (colore, tonalizzante, taglio, schiariture, ricostruzione…). Sovrascrivono i valori generali sopra.</p>
                </div>
                {Object.entries(booking.recall?.per_service || {}).map(([sid, ov]) => {
                  const svc = services.find((s) => s.id === sid);
                  return (
                    <div key={sid} className="flex items-center gap-2 flex-wrap" data-testid={`recall-ps-${sid}`}>
                      <span className="text-sm min-w-32 flex-1 truncate">{svc?.name || sid}</span>
                      <span className="text-xs text-muted-foreground">1º</span>
                      <Input type="number" className="w-20" value={ov.first_days ?? 25} onChange={(e) => { const ps = { ...(booking.recall?.per_service || {}) }; ps[sid] = { ...ps[sid], first_days: Number(e.target.value) || 0 }; setBooking({ ...booking, recall: { ...booking.recall, per_service: ps } }); }} data-testid={`recall-ps-first-${sid}`} />
                      <span className="text-xs text-muted-foreground">2º</span>
                      <Input type="number" className="w-20" value={ov.second_days ?? 33} onChange={(e) => { const ps = { ...(booking.recall?.per_service || {}) }; ps[sid] = { ...ps[sid], second_days: Number(e.target.value) || 0 }; setBooking({ ...booking, recall: { ...booking.recall, per_service: ps } }); }} data-testid={`recall-ps-second-${sid}`} />
                      <Switch checked={ov.enabled !== false} onCheckedChange={(v) => { const ps = { ...(booking.recall?.per_service || {}) }; ps[sid] = { ...ps[sid], enabled: v }; setBooking({ ...booking, recall: { ...booking.recall, per_service: ps } }); }} data-testid={`recall-ps-enabled-${sid}`} />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { const ps = { ...(booking.recall?.per_service || {}) }; delete ps[sid]; setBooking({ ...booking, recall: { ...booking.recall, per_service: ps } }); }} data-testid={`recall-ps-del-${sid}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  );
                })}
                <select className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm" value="" onChange={(e) => { const sid = e.target.value; if (!sid) return; const ps = { ...(booking.recall?.per_service || {}) }; if (!ps[sid]) ps[sid] = { first_days: booking.recall?.first_days ?? 25, second_days: booking.recall?.second_days ?? 33, enabled: true }; setBooking({ ...booking, recall: { ...booking.recall, per_service: ps } }); }} data-testid="recall-ps-add">
                  <option value="">+ Aggiungi servizio…</option>
                  {services.filter((s) => !(booking.recall?.per_service || {})[s.id]).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>}
              {canWrite && <div className="flex gap-2">
                <Button onClick={async () => { await saveBooking(); }} data-testid="recall-save">Salva</Button>
                <Button variant="outline" onClick={async () => { try { const r = await api.post("/automations/recalls/run"); toast.success(`Richiami eseguiti: ${r.data.queued} in coda`); loadComms(); } catch { toast.error("Errore esecuzione richiami"); } }} data-testid="recall-run">Esegui ora</Button>
              </div>}
            </Card>
          )}
          {booking && (
            <Card className="p-5 mb-4 max-w-2xl space-y-4" data-testid="automations-card">
              <div>
                <h3 className="font-heading text-lg font-semibold">Automazioni & prova template</h3>
                <p className="text-sm text-muted-foreground">Attiva o disattiva singolarmente ogni automazione e invia un messaggio di prova prima dell'attivazione.</p>
              </div>
              <div className="space-y-2">
                {(booking.reminders || []).map((ev, i) => {
                  const labels = { booking_confirmed: "Conferma prenotazione", reminder: "Promemoria appuntamento", review_request: "Richiesta recensione" };
                  const offs = ev.offset_hours ? `${ev.offset_hours > 0 ? "+" : ""}${ev.offset_hours}h` : "immediato";
                  return (
                    <div key={i} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`automation-row-${i}`}>
                      <div>
                        <Label className="capitalize">{labels[ev.event] || ev.event.replace(/_/g, " ")} <span className="text-[11px] text-muted-foreground">· {offs} · {(ev.channels || []).join(", ")}</span></Label>
                      </div>
                      <Switch checked={ev.enabled !== false} disabled={!canWrite} onCheckedChange={(v) => { const rem = [...booking.reminders]; rem[i] = { ...rem[i], enabled: v }; setBooking({ ...booking, reminders: rem }); }} data-testid={`automation-toggle-${i}`} />
                    </div>
                  );
                })}
              </div>
              {canWrite && <div className="rounded-md border border-border p-3 space-y-3">
                <div className="text-sm font-medium">Invia un messaggio di prova</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Template</Label>
                    <select className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm" value={testEvent} onChange={(e) => setTestEvent(e.target.value)} data-testid="test-event-select">
                      <option value="booking_confirmed">Conferma prenotazione</option>
                      <option value="reminder">Promemoria appuntamento</option>
                      <option value="review_request">Richiesta recensione</option>
                      <option value="recall">Richiamo post-servizio</option>
                      <option value="birthday">Auguri compleanno</option>
                    </select>
                  </div>
                  <div className="space-y-1.5"><Label>Canale</Label>
                    <select className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm" value={testChannel} onChange={(e) => setTestChannel(e.target.value)} data-testid="test-channel-select">
                      <option value="email">Email</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </div>
                  <div className="space-y-1.5"><Label>{testChannel === "whatsapp" ? "Telefono prova" : "Email prova"}</Label><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={testChannel === "whatsapp" ? "+39 333 1234567" : "tua@email.it"} data-testid="test-to-input" /></div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveBooking} variant="outline" data-testid="automations-save">Salva automazioni</Button>
                  <Button disabled={testBusy} data-testid="test-send-btn" onClick={async () => {
                    if (testChannel === "email" && (!testTo || !testTo.includes("@"))) { toast.error("Inserisci un'email valida"); return; }
                    if (testChannel === "whatsapp" && !testTo.trim()) { toast.error("Inserisci un numero di telefono"); return; }
                    setTestBusy(true);
                    try { const r = await api.post("/communications/send-test", { event: testEvent, channel: testChannel, to: testTo }); toast.success(`Prova inviata${r.data.simulated ? " (simulata in preview)" : ""}: "${r.data.subject || testEvent}"`); loadComms(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio prova"); }
                    finally { setTestBusy(false); }
                  }}>Invia prova</Button>
                </div>
                <p className="text-[10px] text-muted-foreground">In Preview l'invio è simulato (sandbox). In Produzione, con Resend/WhatsApp attivi, il messaggio arriva realmente.</p>
              </div>}
            </Card>
          )}
          <Card className="p-0">
            <div className="p-4 border-b border-border"><h3 className="font-heading text-xl font-semibold">Coda comunicazioni</h3><p className="text-sm text-muted-foreground">Eventi generati dalle prenotazioni. In Test mode gli invii sono simulati (provider ":test").</p></div>
            <Table>
              <TableHeader><TableRow><TableHead>Evento</TableHead><TableHead>Canale</TableHead><TableHead>Cliente</TableHead><TableHead>Programmato</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {comms.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessuna comunicazione in coda</TableCell></TableRow>}
                {comms.slice(0, 60).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="capitalize">{(c.event || "").replace(/_/g, " ")}</TableCell>
                    <TableCell className="capitalize">{c.channel}</TableCell>
                    <TableCell>{c.client_name}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{c.scheduled_for ? new Date(c.scheduled_for).toLocaleString("it-IT") : "—"}</TableCell>
                    <TableCell><Badge variant={c.status === "failed" ? "destructive" : "secondary"} className="capitalize">{c.status.replace("_", " ")}</Badge></TableCell>
                    <TableCell>{canWrite && ["failed", "queued"].includes(c.status) && <Button variant="ghost" size="sm" onClick={() => retryComm(c)} data-testid={`comm-retry-${c.id}`}>Reinvia</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {user?.role === "admin" && (
          <TabsContent value="email" className="mt-4">
            <div className="space-y-4">
              <Card className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading text-lg font-semibold">Dominio e mittente</h3>
                  {email && <Badge variant={email.sandbox ? "secondary" : "default"} data-testid="email-mode-badge">{email.sandbox ? "SANDBOX (test)" : "LIVE"}</Badge>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Dominio email</Label><Input value={settings?.email_domain || ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, email_domain: e.target.value })} placeholder="app.hairidentityrende.it" data-testid="email-domain" /></div>
                  <div className="space-y-1.5"><Label>Provider</Label><Input value={email?.provider || "Resend"} disabled /></div>
                  <div className="space-y-1.5"><Label>Mittente no-reply</Label><Input value={settings?.email_sender_no_reply || ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, email_sender_no_reply: e.target.value })} placeholder="no-reply@app.hairidentityrende.it" data-testid="email-sender-noreply" /></div>
                  <div className="space-y-1.5"><Label>Mittente prenotazioni</Label><Input value={settings?.email_sender_prenotazioni || ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, email_sender_prenotazioni: e.target.value })} placeholder="prenotazioni@app.hairidentityrende.it" data-testid="email-sender-booking" /></div>
                </div>
                <div className="mt-4">
                  <Label className="text-sm">Verifiche DNS</Label>
                  <p className="text-xs text-muted-foreground mb-2">Segna come verificato dopo aver configurato i record presso il provider DNS (SPF, DKIM, DMARC).</p>
                  <div className="flex flex-wrap gap-4">
                    {[["email_spf_ok", "SPF"], ["email_dkim_ok", "DKIM"], ["email_dmarc_ok", "DMARC"]].map(([k, lbl]) => (
                      <div key={k} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                        <span className="text-sm">{lbl}</span>
                        <Switch checked={!!settings?.[k]} disabled={!canWrite} onCheckedChange={(v) => setSettings({ ...settings, [k]: v })} data-testid={`dns-${lbl.toLowerCase()}`} />
                      </div>
                    ))}
                  </div>
                </div>
                {canWrite && <Button className="mt-4" onClick={saveEmailConfig} data-testid="email-save">Salva configurazione</Button>}
                <p className="text-xs text-muted-foreground mt-3">Nota: dominio, SSL e record DNS vanno configurati presso il provider (attività esterna al gestionale). L'invio reale richiede il dominio verificato su Resend.</p>
              </Card>

              <Card className="p-5">
                <h3 className="font-heading text-lg font-semibold mb-3">Invio di prova</h3>
                <div className="flex gap-2 items-end">
                  <div className="space-y-1.5 flex-1"><Label>Indirizzo destinatario</Label><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tua@email.it" data-testid="email-test-to" /></div>
                  <Button variant="outline" onClick={sendTestEmail} data-testid="email-test-send">Invia email di prova</Button>
                </div>
                {email && (
                  <div className="flex gap-4 mt-4 text-sm">
                    <span data-testid="email-stat-queued">In coda: <b>{email.stats.queued}</b></span>
                    <span data-testid="email-stat-sent">Inviate: <b>{email.stats.sent}</b></span>
                    <span data-testid="email-stat-failed" className="text-destructive">Fallite: <b>{email.stats.failed}</b></span>
                  </div>
                )}
              </Card>

              <Card className="p-5">
                <h3 className="font-heading text-lg font-semibold mb-3">Auguri di compleanno automatici</h3>
                <div className="flex items-center justify-between rounded-md border border-border p-3 mb-3">
                  <div><div className="text-sm font-medium">Invio automatico giornaliero</div><div className="text-xs text-muted-foreground">Invia gli auguri alle clienti che compiono gli anni (rispetta il consenso)</div></div>
                  <Switch checked={settings?.birthday_greetings_enabled !== false} disabled={!canWrite} onCheckedChange={(v) => setSettings({ ...settings, birthday_greetings_enabled: v })} data-testid="birthday-enabled" />
                </div>
                <div className="space-y-1.5"><Label>Messaggio (usa {"{nome}"} e {"{salone}"})</Label><Input value={settings?.birthday_greeting_template || ""} disabled={!canWrite} onChange={(e) => setSettings({ ...settings, birthday_greeting_template: e.target.value })} placeholder="Tanti auguri {nome}! Il team di {salone}..." data-testid="birthday-template" /></div>
                {canWrite && <div className="flex gap-2 mt-3"><Button onClick={saveEmailConfig} data-testid="birthday-save">Salva</Button><Button variant="outline" onClick={runBirthdays} data-testid="birthday-run">Invia auguri di oggi</Button></div>}
              </Card>

              {email && (
                <Card className="p-5">
                  <h3 className="font-heading text-lg font-semibold mb-3">Log email recenti</h3>
                  <Table>
                    <TableHeader><TableRow><TableHead>Destinatario</TableHead><TableHead>Oggetto</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {email.log.length === 0 && <TableRow><TableCell colSpan={3} className="text-muted-foreground">Nessuna email</TableCell></TableRow>}
                      {email.log.map((c) => (
                        <TableRow key={c.id} data-testid={`email-log-${c.id}`}>
                          <TableCell className="truncate max-w-[160px]">{c.client_name}</TableCell>
                          <TableCell className="truncate max-w-[220px]">{c.subject}</TableCell>
                          <TableCell><Badge variant={["sent", "delivered"].includes(c.status) ? "default" : c.status === "queued" ? "secondary" : "destructive"}>{c.status}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </div>
          </TabsContent>
        )}


        {user?.role === "admin" && (
          <TabsContent value="permessi" className="mt-4">
            <RolesMatrix />
          </TabsContent>
        )}
        {user?.role === "admin" && (
          <TabsContent value="manutenzione" className="mt-4 space-y-6">
            <DataBackup />
            <ConfigSync />
            <DangerZone />
          </TabsContent>
        )}
        {(user?.role === "admin" || user?.role === "manager") && (
          <TabsContent value="referral" className="mt-4">
            <ReferralSettingsPanel canWrite={user?.role === "admin"} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

const ROLE_LABEL = { manager: "Manager", reception: "Reception", stylist: "Hair Stylist", assistant: "Assistente" };

function DataBackup() {
  const [busy, setBusy] = useState(false);
  const doBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/data/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `hair-identity-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const c = data?.counts || {};
      toast.success(`Backup scaricato: ${c.clients || 0} clienti, ${c.appointments || 0} appuntamenti, ${c.sales || 0} vendite`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore backup");
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 max-w-2xl space-y-4" data-testid="data-backup-card">
      <div>
        <h3 className="font-heading text-lg font-semibold">Backup dati</h3>
        <p className="text-sm text-muted-foreground">Scarica una copia completa dei dati del salone (clienti, appuntamenti, vendite, pacchetti, listino, team). Operazione di sola lettura: non modifica nulla. Le password non vengono mai esportate.</p>
      </div>
      <Button onClick={doBackup} disabled={busy} data-testid="data-backup-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Scarica backup (JSON)
      </Button>
    </Card>
  );
}

function RolesMatrix() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/rbac/matrix").then((r) => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data.permissions))); });
  useEffect(() => { load(); }, []);
  if (!data) return <Card className="p-8 text-center text-muted-foreground text-sm">Caricamento matrice…</Card>;
  const toggle = (role, key) => setDraft((d) => {
    const set = new Set(d[role] || []);
    set.has(key) ? set.delete(key) : set.add(key);
    return { ...d, [role]: [...set] };
  });
  const saveRole = async (role) => {
    setSaving(true);
    try { await api.put(`/rbac/roles/${role}`, { permissions: draft[role] }); toast.success(`Permessi '${ROLE_LABEL[role]}' salvati`); load(); }
    catch { toast.error("Errore salvataggio"); } finally { setSaving(false); }
  };
  const resetAll = async () => { await api.post("/rbac/reset"); toast.success("Ripristinati i permessi predefiniti"); load(); };
  return (
    <Card className="p-0 overflow-hidden" data-testid="rbac-matrix">
      <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-xl font-semibold">Matrice ruoli & permessi</h3>
          <p className="text-sm text-muted-foreground">L'Admin ha sempre accesso completo. I dati economici sono riservati salvo concessione esplicita.</p>
        </div>
        <Button variant="outline" size="sm" onClick={resetAll} data-testid="rbac-reset">Ripristina predefiniti</Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[220px]">Modulo / Permesso</TableHead>
              {data.roles.map((r) => <TableHead key={r} className="text-center">{ROLE_LABEL[r] || r}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.modules.map((m) => (
              (m.actions || ["read"]).map((a) => {
                const key = `${m.key}.${a}`;
                return (
                  <TableRow key={key} data-testid={`rbac-row-${key}`}>
                    <TableCell>
                      <span className="font-medium">{m.label}</span>
                      <span className="text-xs text-muted-foreground ml-1.5">{a}</span>
                      {m.econ && <Badge variant="outline" className="ml-2 text-[10px] border-[hsl(var(--hi-gold))] text-[hsl(var(--hi-gold))]">economico</Badge>}
                    </TableCell>
                    {data.roles.map((role) => (
                      <TableCell key={role} className="text-center">
                        <input type="checkbox" checked={(draft[role] || []).includes(key)}
                          onChange={() => toggle(role, key)}
                          data-testid={`rbac-${role}-${key}`}
                          className="h-4 w-4 accent-[hsl(var(--hi-gold))] cursor-pointer" />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="p-4 border-t border-border flex flex-wrap gap-2 justify-end">
        {data.roles.map((r) => (
          <Button key={r} size="sm" disabled={saving} onClick={() => saveRole(r)} data-testid={`rbac-save-${r}`}>Salva {ROLE_LABEL[r] || r}</Button>
        ))}
      </div>
    </Card>
  );
}

function ConfigSync() {
  const [exporting, setExporting] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [opts, setOpts] = useState({ services: true, booking_config: true, salon_hours: true });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/config/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `hair-identity-config-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Configurazione esportata (${data?.counts?.services ?? 0} servizi)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore esportazione");
    } finally { setExporting(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setReport(null);
    if (!f) { setFileData(null); setFileName(""); return; }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (parsed?.source !== "identity_os_config") {
          toast.error("File non valido: non è un export di configurazione");
          setFileData(null); setFileName("");
          return;
        }
        setFileData(parsed); setFileName(f.name);
        toast.success(`File caricato: ${parsed?.counts?.services ?? 0} servizi`);
      } catch {
        toast.error("File JSON non leggibile");
        setFileData(null); setFileName("");
      }
    };
    reader.readAsText(f);
  };

  const runImport = async (dry) => {
    if (!fileData) { toast.error("Carica prima un file di configurazione"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/config/import", { data: fileData, options: opts, dry_run: dry });
      setReport(data);
      if (dry) toast.success("Anteprima calcolata: verifica il riepilogo");
      else toast.success("Configurazione importata con successo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore importazione");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="config-sync-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--hi-gold)/0.15)] text-[hsl(var(--hi-gold))]">
          <RefreshCw className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold">Sincronizza configurazione</h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Copia servizi, automatismi (es. servizi obbligatori/extra), prezzi, durate, regole di
            prenotazione e orari salone da un ambiente all'altro. Non tocca clienti, vendite, staff o turni.
          </p>
        </div>
      </div>

      {/* Esporta */}
      <div className="rounded-lg border border-border p-4 mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-medium">1 · Esporta (dall'ambiente di prova)</div>
            <div className="text-xs text-muted-foreground">Scarica un file con l'intera configurazione attuale.</div>
          </div>
          <Button onClick={doExport} disabled={exporting} data-testid="config-export-btn" className="gap-2">
            <Download className="h-4 w-4" /> {exporting ? "Esporto…" : "Esporta configurazione"}
          </Button>
        </div>
      </div>

      {/* Importa */}
      <div className="rounded-lg border border-border p-4">
        <div className="text-sm font-medium mb-1">2 · Importa (nell'ambiente di destinazione)</div>
        <div className="text-xs text-muted-foreground mb-3">
          Carica il file esportato, controlla l'anteprima e applica. L'aggiornamento avviene per nome del
          servizio (nessun duplicato, nessun collegamento rotto).
        </div>

        <input
          type="file" accept="application/json,.json" onChange={onFile}
          data-testid="config-import-file"
          className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-[hsl(var(--hi-gold-soft))] file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground hover:file:bg-[hsl(var(--hi-gold)/0.25)] cursor-pointer"
        />
        {fileName && <p className="text-xs text-muted-foreground mt-1.5">File: {fileName}</p>}

        <div className="flex flex-wrap gap-4 mt-4">
          {[["services", "Servizi & automatismi"], ["booking_config", "Regole prenotazione"], ["salon_hours", "Orari salone"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={opts[k]} onCheckedChange={(v) => setOpts((o) => ({ ...o, [k]: v }))} data-testid={`config-opt-${k}`} />
              {label}
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" onClick={() => runImport(true)} disabled={!fileData || busy} data-testid="config-import-preview-btn" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Anteprima
          </Button>
          <Button onClick={() => runImport(false)} disabled={!fileData || busy || !report} data-testid="config-import-apply-btn" className="gap-2">
            <Upload className="h-4 w-4" /> Applica alla destinazione
          </Button>
        </div>
        {!report && fileData && (
          <p className="text-[11px] text-muted-foreground mt-2">Esegui prima l'Anteprima, poi si abilita "Applica".</p>
        )}

        {report && (
          <div className="mt-4 rounded-lg bg-muted/50 p-4 text-sm" data-testid="config-import-report">
            <div className="flex items-center gap-2 font-medium mb-2">
              {report.dry_run
                ? <><RefreshCw className="h-4 w-4 text-[hsl(var(--hi-status-info,210_90%_50%))]" /> Anteprima (nessuna modifica applicata)</>
                : <><CheckCircle2 className="h-4 w-4 text-[hsl(var(--hi-status-success))]" /> Importazione completata</>}
            </div>
            <ul className="space-y-1 text-muted-foreground">
              <li>Servizi creati: <b className="text-foreground">{report.services_created}</b></li>
              <li>Servizi aggiornati: <b className="text-foreground">{report.services_updated}</b></li>
              <li>Automatismi impostati: <b className="text-foreground">{report.deps_set}</b></li>
              <li>Regole prenotazione: <b className="text-foreground">{report.booking_config_applied ? "sì" : "no"}</b> · Orari salone: <b className="text-foreground">{report.salon_hours_applied ? "sì" : "no"}</b></li>
            </ul>
            {report.deps_skipped?.length > 0 && (
              <div className="mt-2 text-xs text-[hsl(var(--hi-status-warning,38_92%_50%))]">
                Automatismi saltati (servizio collegato mancante):
                <ul className="list-disc pl-5 mt-1">
                  {report.deps_skipped.map((d, i) => (
                    <li key={`${d.service}-${d.missing_dependency}-${i}`}>{d.service} → {d.missing_dependency}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}


function DangerZone() {
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [locName, setLocName] = useState("Hair Identity — Rende");
  const [busy, setBusy] = useState(false);

  const runPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/reset-demo", { dry_run: true, location_name: locName }); setPreview(data.would_delete); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(false); }
  };
  const runReset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reset-demo", { confirm: "RESET", location_name: locName });
      toast.success("Reset completato. Ricarico l'applicazione…");
      setTimeout(() => window.location.reload(), 1400);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante il reset"); setBusy(false); }
  };
  const total = preview ? Object.values(preview).reduce((s, v) => s + v, 0) : 0;

  const loadPricelist = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/load-pricelist", {});
      toast.success(`Listino caricato: ${data.inserted} servizi aggiunti, ${data.skipped} già presenti`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento listino"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 max-w-2xl border-[hsl(var(--hi-status-danger)/0.5)]" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-5 w-5 text-[hsl(var(--hi-status-danger))]" />
        <h3 className="font-heading text-xl font-semibold">Reset dati demo</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Elimina tutti i dati demo/test (clienti, appuntamenti, vendite, cassa, magazzino, servizi,
        turni, presenze, campagne, notifiche, ecc.) preservando struttura, configurazioni, permessi
        e il TUO account Admin. Aggiorna anche il nome della sede. <b>Operazione irreversibile.</b>
      </p>
      <div className="space-y-1.5 mb-3 max-w-sm">
        <Label>Nome sede</Label>
        <Input value={locName} onChange={(e) => setLocName(e.target.value)} data-testid="reset-location-name" />
      </div>
      <Button variant="outline" onClick={runPreview} disabled={busy} data-testid="reset-preview-btn">Anteprima (dry-run)</Button>

      <div className="mt-6 border-t border-border pt-4">
        <h4 className="font-medium mb-1">Carica listino Hair Identity (Rende)</h4>
        <p className="text-sm text-muted-foreground mb-3">Inserisce i servizi ufficiali del listino (idempotente: salta quelli già presenti).</p>
        <Button variant="secondary" onClick={loadPricelist} disabled={busy} data-testid="load-pricelist-btn">Carica listino servizi</Button>
      </div>

      {preview && (
        <div className="mt-4 rounded-lg border border-border p-3 text-sm">
          <div className="font-medium mb-2">Verranno eliminati {total} record demo:</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-48 overflow-y-auto hi-scroll">
            {Object.entries(preview).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular">{v}</span></div>
            ))}
            {total === 0 && <div className="text-muted-foreground col-span-2">Nessun dato demo da eliminare.</div>}
          </div>
          <div className="mt-4 border-t border-border pt-3 space-y-2 max-w-sm">
            <Label>Digita <b>RESET</b> per confermare</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" data-testid="reset-confirm-input" />
            <Button variant="destructive" disabled={confirmText !== "RESET" || busy} onClick={runReset} data-testid="reset-execute-btn">
              Esegui reset definitivo
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


// Test Tecnico WhatsApp — invia UN SOLO messaggio ad un numero specifico (chiamata REALE Meta).
// Separato dalle Campagne per evitare invii accidentali di massa.
function WhatsAppTest({ defaultCc }) {
  const [to, setTo] = useState("");
  const [templates, setTemplates] = useState(null); // null = non caricati
  const [loadingTpls, setLoadingTpls] = useState(false);
  const [tplError, setTplError] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [vars, setVars] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [wh, setWh] = useState(null); // {callback_url_*, path}
  const [showToken, setShowToken] = useState(false);
  const [verifyToken, setVerifyToken] = useState("");
  // Verifica connessione produzione + Go-Live
  const [conn, setConn] = useState(null);       // risultato verifica connessione Meta
  const [verifying, setVerifying] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [goingLive, setGoingLive] = useState(false);

  const verifyConnection = async () => {
    setVerifying(true); setConn(null);
    try {
      const { data } = await api.post("/integrations/whatsapp/verify-connection");
      setConn(data);
      if (data.ok) toast.success(`Connessione OK · ${data.verified_name || data.display_phone_number || "numero verificato"}`);
      else toast.error(data.error?.message || `Verifica fallita (HTTP ${data.http_status})`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore verifica connessione";
      setConn({ ok: false, http_status: e?.response?.status || 0, error: { message: detail } });
      toast.error(detail);
    } finally { setVerifying(false); }
  };

  const goLive = async () => {
    setGoingLive(true);
    try {
      const { data } = await api.post("/integrations/whatsapp/go-live");
      if (data.activated) {
        setConn(data.verify);
        setGoLiveOpen(false);
        toast.success("Produzione attiva · gli invii WhatsApp ora sono REALI");
        window.dispatchEvent(new Event("integrations:refresh"));
      } else {
        setConn(data.verify);
        toast.error(data.verify?.error?.message || "Verifica fallita: produzione non attivata");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore attivazione produzione");
    } finally { setGoingLive(false); }
  };

  useEffect(() => {
    api.get("/integrations/whatsapp/webhook-info").then((r) => setWh(r.data)).catch(() => {});
  }, []);

  const revealToken = async () => {
    try {
      const { data } = await api.get("/integrations/whatsapp/webhook-info");
      setVerifyToken(data.verify_token || "");
      setShowToken(true);
    } catch { toast.error("Impossibile recuperare il Verify Token"); }
  };
  const copyText = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copiato`); }
    catch { toast.error("Copia non riuscita"); }
  };

  const selected = (templates || []).find((t) => t.name === selectedName) || null;
  const phoneValid = /^\+?[1-9]\d{7,14}$/.test(to.replace(/\s/g, ""));

  const loadTemplates = async () => {
    setLoadingTpls(true); setTplError(null);
    try {
      const { data } = await api.get("/integrations/whatsapp/templates");
      if (data.ok) {
        const list = (data.templates || []).filter((t) => (t.status || "").toUpperCase() === "APPROVED");
        setTemplates(list);
        if (list.length === 0) setTplError("Nessun template APPROVED trovato sul tuo account WhatsApp.");
        else toast.success(`${list.length} template approvati caricati`);
      } else {
        const e = data.error || {};
        setTemplates([]);
        setTplError(e.message || `Errore Meta (HTTP ${data.http_status})`);
      }
    } catch (e) {
      setTemplates([]);
      setTplError(e?.response?.data?.detail || "Errore nel caricamento dei template");
    } finally { setLoadingTpls(false); }
  };

  const onSelectTemplate = (name) => {
    setSelectedName(name);
    const t = (templates || []).find((x) => x.name === name);
    const n = t?.body_param_count || 0;
    setVars(Array.from({ length: n }, () => ""));
    setResult(null);
  };

  const sendTest = async () => {
    if (!phoneValid) { toast.error("Inserisci un numero valido in formato internazionale (es. +393331234567)"); return; }
    if (!selectedName) { toast.error("Seleziona un template approvato"); return; }
    if (vars.some((v) => !String(v).trim())) { toast.error("Compila tutte le variabili del template"); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/integrations/whatsapp/test-send", {
        to: to.replace(/\s/g, ""),
        template_name: selectedName,
        language_code: selected?.language || "it",
        body_parameters: vars,
      });
      setResult(data);
      if (data.ok) toast.success(`Messaggio inviato · ID: ${data.message_id?.slice(0, 18)}…`);
      else toast.error(data.error?.message || `Invio fallito (HTTP ${data.http_status})`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore invio";
      setResult({ ok: false, http_status: e?.response?.status || 0, error: { message: detail } });
      toast.error(detail);
    } finally { setSending(false); }
  };

  return (
    <Card className="p-5 mt-4 border-[hsl(var(--hi-gold)/0.4)]" data-testid="whatsapp-test-card">
      <div className="flex items-start gap-3 mb-1">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--hi-gold)/0.15)] text-[hsl(var(--hi-gold))]">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold">WhatsApp — Test tecnico</h3>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Invia <b>un solo messaggio</b> ad <b>un numero specifico</b> per verificare l'integrazione. Fa una
            chiamata reale alle API Meta e mostra l'esito reale (Message ID o errore). <b>Non</b> invia messaggi in massa.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Numero di test */}
        <div className="space-y-1.5">
          <Label>Numero di test (formato internazionale)</Label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+393331234567"
            data-testid="wa-test-phone" className={!to || phoneValid ? "" : "border-[hsl(var(--hi-status-danger))]"} />
          {to && !phoneValid && <p className="text-[11px] text-[hsl(var(--hi-status-danger))]">Numero non valido. Usa il formato +39...</p>}
          {defaultCc && <p className="text-[11px] text-muted-foreground">Prefisso predefinito applicato ai numeri senza "+": {defaultCc}</p>}
        </div>

        {/* Template */}
        <div className="space-y-1.5">
          <Label>Template approvato</Label>
          <div className="flex gap-2">
            <Select value={selectedName} onValueChange={onSelectTemplate} disabled={!templates || templates.length === 0}>
              <SelectTrigger data-testid="wa-test-template"><SelectValue placeholder={templates ? "Seleziona…" : "Carica prima i template"} /></SelectTrigger>
              <SelectContent>
                {(templates || []).map((t) => (
                  <SelectItem key={`${t.name}-${t.language}`} value={t.name}>{t.name} · {t.language}{t.body_param_count ? ` · ${t.body_param_count} var` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadTemplates} disabled={loadingTpls} data-testid="wa-test-load-templates" className="shrink-0">
              {loadingTpls ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
          {tplError && <p className="text-[11px] text-[hsl(var(--hi-status-danger))]" data-testid="wa-test-tpl-error">{tplError}</p>}
        </div>
      </div>

      {/* Variabili dinamiche */}
      {selected && vars.length > 0 && (
        <div className="mt-4 rounded-lg border border-border p-3 space-y-2" data-testid="wa-test-vars">
          <div className="text-xs font-medium text-muted-foreground">Variabili del template (in ordine {"{{1}}, {{2}}"}…)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {vars.map((v, i) => (
              <div key={i} className="space-y-1">
                <Label className="text-[11px]">Variabile {"{{" + (i + 1) + "}}"}</Label>
                <Input value={v} onChange={(e) => setVars((a) => a.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder={`Valore ${i + 1}`} data-testid={`wa-test-var-${i}`} className="h-8 text-xs" />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button onClick={sendTest} disabled={sending || !phoneValid || !selectedName} data-testid="wa-test-send" className="gap-1.5">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Invia test (1 messaggio)
        </Button>
        <span className="text-[11px] text-muted-foreground">Richiede credenziali WhatsApp (Access Token, Phone Number ID) e WABA ID per i template.</span>
      </div>

      {/* Verifica connessione & Vai in produzione (Passaggio 1) */}
      <div className="mt-5 rounded-lg border border-[hsl(var(--hi-gold)/0.4)] bg-[hsl(var(--hi-gold)/0.05)] p-3 space-y-3" data-testid="wa-golive-panel">
        <div className="flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 text-[hsl(var(--hi-gold))] shrink-0" />
          <div>
            <div className="text-sm font-medium">Connessione & Produzione (Passaggio 1 — Vai in produzione)</div>
            <p className="text-[11px] text-muted-foreground">Verifica che Access Token e Phone Number ID siano validi presso Meta, poi attiva la produzione. In produzione gli invii WhatsApp sono <b>reali</b>.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={verifyConnection} disabled={verifying} data-testid="wa-verify-connection" className="gap-1.5">
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Verifica connessione
          </Button>
          <Button size="sm" onClick={() => setGoLiveOpen(true)} disabled={!conn?.ok} data-testid="wa-go-live" className="gap-1.5">
            <Zap className="h-4 w-4" />
            Vai in produzione
          </Button>
          {!conn?.ok && <span className="text-[11px] text-muted-foreground">Verifica la connessione prima di attivare la produzione.</span>}
        </div>

        {conn && (
          <div className={`rounded-md border p-2.5 text-xs ${conn.ok ? "border-[hsl(var(--hi-status-success)/0.5)] bg-[hsl(var(--hi-status-success)/0.08)]" : "border-[hsl(var(--hi-status-danger)/0.5)] bg-[hsl(var(--hi-status-danger)/0.08)]"}`} data-testid="wa-conn-result">
            {conn.ok ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--hi-status-success))]" /> Connessione verificata con Meta</div>
                <div><span className="text-muted-foreground">Nome business: </span><b data-testid="wa-conn-name">{conn.verified_name || "—"}</b></div>
                <div><span className="text-muted-foreground">Numero: </span><b>{conn.display_phone_number || "—"}</b></div>
                <div><span className="text-muted-foreground">Quality rating: </span><b>{conn.quality_rating || "—"}</b></div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2 font-medium"><XCircle className="h-4 w-4 text-[hsl(var(--hi-status-danger))]" /> Verifica fallita</div>
                {conn.error?.code != null && <div><span className="text-muted-foreground">Codice: </span><b>{conn.error.code}</b></div>}
                <div><span className="text-muted-foreground">Messaggio: </span><b className="text-[hsl(var(--hi-status-danger))]">{conn.error?.message || "—"}</b></div>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={goLiveOpen} onOpenChange={setGoLiveOpen}>
        <DialogContent data-testid="wa-golive-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Attivare la produzione WhatsApp?</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Attivando la produzione, i messaggi WhatsApp (promemoria, conferme, post-visita, ecc.) verranno <b>inviati realmente</b> ai clienti tramite Meta Cloud API con il numero <b>{conn?.display_phone_number || conn?.verified_name || "verificato"}</b>. La modalità Sandbox verrà disattivata.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoLiveOpen(false)} data-testid="wa-golive-abort">Annulla</Button>
            <Button onClick={goLive} disabled={goingLive} data-testid="wa-golive-confirm">{goingLive && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Attiva produzione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webhook Meta — configurazione produzione */}
      <div className="mt-5 rounded-lg border border-border p-3 space-y-3" data-testid="wa-webhook-panel">
        <div>
          <div className="text-sm font-medium">Webhook Meta (Passaggio 2 — Configurazione di produzione)</div>
          <p className="text-[11px] text-muted-foreground">Copia questi valori nel pannello Meta: WhatsApp → Configurazione → Webhook. Sottoscrivi l'evento <b>messages</b>.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">URL di callback</Label>
          <div className="flex gap-2">
            <Input readOnly value={wh?.callback_url_production || ""} data-testid="wa-webhook-callback" className="h-8 text-xs font-mono" />
            <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => copyText(wh?.callback_url_production || "", "URL di callback")} data-testid="wa-webhook-copy-url">Copia</Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Verifica il token (Verify Token)</Label>
          <div className="flex gap-2">
            <Input readOnly type={showToken ? "text" : "password"} value={showToken ? verifyToken : "••••••••••••••••••••"} data-testid="wa-webhook-verify-token" className="h-8 text-xs font-mono" />
            {!showToken ? (
              <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={revealToken} data-testid="wa-webhook-reveal-token">Mostra</Button>
            ) : (
              <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => copyText(verifyToken, "Verify Token")} data-testid="wa-webhook-copy-token">Copia</Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">Il Verify Token è salvato solo lato server; viene mostrato solo su richiesta a un Admin.</p>
        </div>
      </div>

      {/* Pannello risultato */}
      {result && (
        <div className={`mt-4 rounded-lg border p-3 text-sm ${result.ok ? "border-[hsl(var(--hi-status-success)/0.5)] bg-[hsl(var(--hi-status-success)/0.08)]" : "border-[hsl(var(--hi-status-danger)/0.5)] bg-[hsl(var(--hi-status-danger)/0.08)]"}`} data-testid="wa-test-result">
          <div className="flex items-center gap-2 font-medium mb-2">
            {result.ok
              ? <><CheckCircle2 className="h-4 w-4 text-[hsl(var(--hi-status-success))]" /> Messaggio inviato correttamente</>
              : <><XCircle className="h-4 w-4 text-[hsl(var(--hi-status-danger))]" /> Invio fallito</>}
            <Badge variant="outline" className="ml-auto text-[10px]" data-testid="wa-test-http-status">HTTP {result.http_status}</Badge>
          </div>
          {result.ok ? (
            <div className="space-y-1 text-xs">
              <div><span className="text-muted-foreground">Destinatario: </span><b>{result.to}</b></div>
              <div className="break-all"><span className="text-muted-foreground">Message ID: </span><code className="text-foreground" data-testid="wa-test-message-id">{result.message_id}</code></div>
              <p className="text-[11px] text-muted-foreground mt-1">Se il numero è iscritto correttamente riceverà il messaggio del template selezionato.</p>
            </div>
          ) : (
            <div className="space-y-1 text-xs">
              {result.error?.code != null && <div><span className="text-muted-foreground">Codice: </span><b>{result.error.code}</b></div>}
              <div><span className="text-muted-foreground">Messaggio: </span><b className="text-[hsl(var(--hi-status-danger))]">{result.error?.message || "—"}</b></div>
              {result.error?.type && <div><span className="text-muted-foreground">Tipo: </span>{result.error.type}</div>}
              {result.error?.fbtrace_id && <div className="break-all"><span className="text-muted-foreground">fbtrace_id: </span><code>{result.error.fbtrace_id}</code></div>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Automazione WhatsApp post-visita: selezione del TEMPLATE APPROVED dal WABA,
// lingua, mappatura parametri corpo e anteprima. Salva in
// settings.whatsapp_templates.post_payment = { name, language, param_keys }.
// ---------------------------------------------------------------------------
const PV_VAR_KEYS = [
  { k: "cliente", l: "Nome cliente" },
  { k: "stylist", l: "Stylist / operatore" },
  { k: "salone", l: "Nome salone" },
  { k: "servizi", l: "Servizi svolti" },
  { k: "importo", l: "Importo totale" },
  { k: "metodo", l: "Metodo di pagamento" },
  { k: "link_recensione", l: "Link recensione" },
];
// Variabili disponibili per conferma/promemoria (dal motore appuntamenti)
const VAR_APPT = [
  { k: "nome", l: "Nome cliente" },
  { k: "data", l: "Data appuntamento" },
  { k: "ora", l: "Ora appuntamento" },
  { k: "operatore", l: "Operatore" },
  { k: "servizi", l: "Servizi" },
  { k: "prezzo", l: "Prezzo" },
  { k: "data_giorno", l: "Data con giorno (es. lun 15/09)" },
  { k: "link_recensione", l: "Link recensione" },
  { k: "mappa", l: "Link mappa" },
];
// Variabili generiche (eventi vari: winback, compleanno, ecc.)
const VAR_GENERIC = [
  { k: "nome", l: "Nome cliente" },
  { k: "cliente", l: "Nome cliente (alt.)" },
  { k: "data", l: "Data appuntamento" },
  { k: "ora", l: "Ora appuntamento" },
  { k: "operatore", l: "Operatore" },
  { k: "servizi", l: "Servizi" },
  { k: "prezzo", l: "Prezzo" },
  { k: "importo", l: "Importo totale" },
  { k: "salone", l: "Nome salone" },
  { k: "metodo", l: "Metodo di pagamento" },
  { k: "categoria", l: "Categoria servizio" },
  { k: "giorni_ultimo_servizio", l: "Giorni dall'ultimo servizio" },
  { k: "data_giorno", l: "Data con giorno (es. lun 15/09)" },
  { k: "link_recensione", l: "Link recensione" },
  { k: "mappa", l: "Link mappa" },
];
// Eventi/trigger disponibili per le automazioni WhatsApp (mirror del backend).
const AUTOMATION_EVENTS = [
  { event: "booking_confirmed", label: "Conferma appuntamento", vars: VAR_APPT },
  { event: "reminder", label: "Promemoria 24h", vars: VAR_APPT },
  { event: "reminder_3h", label: "Promemoria 3h", vars: VAR_APPT },
  { event: "post_payment", label: "Ringraziamento post-visita", vars: PV_VAR_KEYS },
  { event: "review_request", label: "Richiesta recensione", vars: VAR_APPT },
  { event: "recall", label: "Richiamo cliente (winback)", vars: VAR_GENERIC },
  { event: "birthday", label: "Auguri compleanno", vars: VAR_GENERIC },
];
const evMeta = (e) => AUTOMATION_EVENTS.find((x) => x.event === e) || { event: e, label: e, vars: VAR_GENERIC };
const FIXED_WA_EVENTS = ["booking_confirmed", "reminder", "reminder_3h", "post_payment"];


function PostVisitTemplate({ event = "post_payment", label, varKeys, pickable = false, eventOptions = [], onPicked, onRemove, settings, setSettings, canWrite, waLive }) {
  const [templates, setTemplates] = useState(null); // null = non caricati
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Etichetta e variabili derivate dall'evento se non passate esplicitamente
  const cardLabel = label || evMeta(event).label || event;
  const vk = varKeys || evMeta(event).vars || VAR_GENERIC;
  const pp = (settings?.whatsapp_templates || {})[event] || {};

  const updatePP = (patch) => setSettings((s) => {
    const tpls = { ...(s?.whatsapp_templates || {}) };
    tpls[event] = { ...(tpls[event] || {}), ...patch };
    return { ...s, whatsapp_templates: tpls };
  });

  const loadTemplates = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/integrations/whatsapp/templates");
      if (data.ok) {
        const list = (data.templates || []).filter((t) => (t.status || "").toUpperCase() === "APPROVED");
        setTemplates(list);
        if (list.length === 0) setError("Nessun template APPROVED trovato sul tuo WABA. Crea un template di ringraziamento/post-visita su Meta (WhatsApp Manager) e attendi l'approvazione.");
        else toast.success(`${list.length} template approvati caricati dal WABA`);
      } else {
        setTemplates([]);
        setError(data.error?.message || `Errore Meta (HTTP ${data.http_status})`);
      }
    } catch (e) {
      setTemplates([]);
      setError(e?.response?.data?.detail || "Errore nel caricamento dei template dal WABA");
    } finally { setLoading(false); }
  };

  const selected = (templates || []).find((t) => t.name === pp.name) || null;
  const bodyText = (() => {
    const comp = (selected?.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
    return comp?.text || "";
  })();
  const paramCount = selected ? (selected.body_param_count || 0) : (pp.param_keys || []).length;

  const onSelect = (name) => {
    const t = (templates || []).find((x) => x.name === name);
    const n = t?.body_param_count || 0;
    const existing = pp.param_keys || [];
    const keys = Array.from({ length: n }, (_, i) => existing[i] || "");
    updatePP({ name, language: t?.language || "it", param_keys: keys });
  };

  const setParam = (idx, key) => {
    const keys = [...(pp.param_keys || [])];
    while (keys.length < paramCount) keys.push("");
    keys[idx] = key;
    updatePP({ param_keys: keys });
  };

  const preview = (() => {
    if (!bodyText) return "";
    return bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
      const idx = parseInt(n, 10) - 1;
      const key = (pp.param_keys || [])[idx];
      const label = vk.find((v) => v.k === key)?.l;
      return label ? `[${label}]` : `{{${n}}}`;
    });
  })();

  const savePP = async () => {
    if (!pp.name) { toast.error("Seleziona un template approvato"); return; }
    try { await api.put("/settings", settings); toast.success("Automazione WhatsApp salvata"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  // Nuova automazione: prima si sceglie l'evento/trigger, poi la scheda si comporta
  // esattamente come le altre (carica template dal WABA, mappa variabili, salva).
  if (pickable && !event) {
    return (
      <div className="rounded-md border border-dashed border-[hsl(var(--hi-gold)/0.5)] bg-[hsl(var(--hi-gold-soft)/0.15)] p-3 space-y-2" data-testid="pv-template-card-new">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Nuova automazione</Label>
          {onRemove && <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove} data-testid="pv-new-remove"><XCircle className="h-4 w-4" /></Button>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Evento / trigger</Label>
          <Select onValueChange={(v) => onPicked && onPicked(v)}>
            <SelectTrigger data-testid="pv-new-event-select"><SelectValue placeholder="Scegli l'evento" /></SelectTrigger>
            <SelectContent>
              {eventOptions.map((o) => <SelectItem key={o.event} value={o.event}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {eventOptions.length === 0 && <p className="text-[11px] text-muted-foreground">Tutti gli eventi disponibili sono già configurati.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[hsl(var(--hi-gold)/0.4)] bg-[hsl(var(--hi-gold-soft)/0.25)] p-3 space-y-3" data-testid={`pv-template-card-${event}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <Label className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> {cardLabel}</Label>
          <p className="text-[11px] text-muted-foreground mt-0.5">Associa un template WhatsApp APPROVED del tuo WABA a questo evento. L'invio avviene automaticamente al verificarsi dell'evento.</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Badge variant="outline" className="text-[10px]" data-testid={`pv-mode-badge-${event}`}>{waLive ? "Produzione" : "Sandbox"}</Badge>
          {onRemove && <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove} data-testid={`pv-remove-${event}`}><XCircle className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={loadTemplates} disabled={loading || !canWrite} data-testid={`pv-load-templates-${event}`}>
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Carica template dal WABA
        </Button>
        {templates && templates.length > 0 && (
          <div className="flex-1 min-w-[220px] space-y-1">
            <Label className="text-xs">Template</Label>
            <Select value={pp.name || ""} onValueChange={onSelect} disabled={!canWrite}>
              <SelectTrigger className="h-9" data-testid={`pv-template-select-${event}`}><SelectValue placeholder="Seleziona template approvato" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={`${t.name}-${t.language}`} value={t.name}>{t.name} · {t.language} {t.category ? `· ${t.category}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {pp.name && <div className="space-y-1"><Label className="text-xs">Lingua</Label><Input value={pp.language || "it"} readOnly className="h-9 w-24" data-testid={`pv-language-${event}`} /></div>}
      </div>

      {!templates && !error && (
        <p className="text-[11px] text-muted-foreground">Premi "Carica template dal WABA" per elencare i template approvati e sceglierne uno.</p>
      )}
      {error && (
        <div className="rounded-md border border-[hsl(var(--hi-status-warning)/0.5)] bg-[hsl(var(--hi-status-warning)/0.08)] p-2 text-xs flex items-start gap-2" data-testid={`pv-template-error-${event}`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--hi-status-warning))] shrink-0" /><span>{error}</span>
        </div>
      )}

      {pp.name && !error && (
        <>
          {!templates && (
            <p className="text-[11px] text-muted-foreground">Template attualmente configurato: <b>{pp.name}</b> ({pp.language || "it"}). Carica dal WABA per modificarlo o vederne l'anteprima.</p>
          )}
          {paramCount > 0 && (
            <div className="space-y-2">
              <Label className="text-xs">Mappatura parametri corpo (in ordine)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Array.from({ length: paramCount }, (_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground w-9">{`{{${i + 1}}}`}</span>
                    <Select value={(pp.param_keys || [])[i] || ""} onValueChange={(v) => setParam(i, v)} disabled={!canWrite}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid={`pv-param-${event}-${i}`}><SelectValue placeholder="Variabile" /></SelectTrigger>
                      <SelectContent>{vk.map((v) => <SelectItem key={v.k} value={v.k}>{v.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
          {preview && (
            <div className="space-y-1">
              <Label className="text-xs">Anteprima messaggio</Label>
              <div className="rounded-md bg-background border border-border p-2.5 text-sm whitespace-pre-wrap" data-testid={`pv-preview-${event}`}>{preview}</div>
            </div>
          )}
        </>
      )}

      {canWrite && <Button type="button" size="sm" onClick={savePP} disabled={!pp.name} data-testid={`pv-save-${event}`}>Salva automazione</Button>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diagnostica invii WhatsApp: mostra gli ultimi tentativi con esito, template,
// numero normalizzato, motivo di blocco e Message ID (wamid) o errore Meta.
// ---------------------------------------------------------------------------
function WhatsAppDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/whatsapp/diagnostics?event=post_payment&limit=10");
      setData(data);
    } catch (e) { toast.error("Errore diagnostica"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const statusBadge = (it) => {
    if (it.blocked_reason) return <Badge className="bg-[hsl(var(--hi-status-danger))]/12 text-[hsl(var(--hi-status-danger))] text-[10px]">Bloccato</Badge>;
    if (it.simulated) return <Badge className="bg-[hsl(var(--hi-status-info))]/12 text-[hsl(var(--hi-status-info))] text-[10px]">Simulato</Badge>;
    if (it.delivered) return <Badge className="bg-[hsl(var(--hi-status-success))]/12 text-[hsl(var(--hi-status-success))] text-[10px]">Inviato</Badge>;
    return <Badge variant="outline" className="text-[10px]">{it.status || "—"}</Badge>;
  };

  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="wa-diagnostics-card">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Diagnostica invii post-visita</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={load} disabled={loading} data-testid="wa-diagnostics-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Aggiorna
        </Button>
      </div>
      {data?.summary && (
        <p className="text-[11px] text-muted-foreground" data-testid="wa-diagnostics-summary">
          Ultimi {data.summary.total} · Inviati {data.summary.delivered} · Bloccati {data.summary.blocked} · Simulati {data.summary.simulated}
        </p>
      )}
      {(!data || data.items.length === 0) ? (
        <p className="text-[11px] text-muted-foreground py-2" data-testid="wa-diagnostics-empty">Nessun invio post-visita registrato finora.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto hi-scroll">
          {data.items.map((it) => (
            <div key={it.id} className="rounded-md border border-border/60 p-2 text-xs space-y-0.5" data-testid={`wa-diag-row-${it.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{it.client_name || "—"}</span>
                {statusBadge(it)}
              </div>
              <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Num: <span className="font-mono">{it.to_phone || "—"}</span></span>
                {it.template && <span>Template: <b>{it.template}</b></span>}
                {it.dispatch_mode && <span>Modo: {it.dispatch_mode}</span>}
              </div>
              {it.wamid && <div className="break-all text-muted-foreground">wamid: <code className="text-foreground">{it.wamid}</code></div>}
              {it.blocked_reason && <div className="text-[hsl(var(--hi-status-danger))]">Motivo blocco: {it.blocked_reason}{it.meta_error ? ` — ${it.meta_error}` : ""}</div>}
              {!it.blocked_reason && it.meta_error && <div className="text-[hsl(var(--hi-status-danger))]">Errore Meta: {it.meta_error}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stato semplificato automazioni WhatsApp (uso quotidiano): mostra solo lo stato
// per evento (attiva / in revisione / da verificare / da configurare), senza
// dettagli tecnici. I dettagli sono nelle "Impostazioni avanzate".
// ---------------------------------------------------------------------------
function WhatsAppAutomationsStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/automations/whatsapp"); setData(data); }
    catch (e) { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const view = (a) => {
    switch (a.state) {
      case "active": return { icon: "✅", text: a.label, cls: "text-foreground", note: "" };
      case "pending": return { icon: "⏳", text: a.label, cls: "text-[hsl(var(--hi-status-warning))]", note: "template Meta in revisione" };
      case "rejected": return { icon: "⚠️", text: a.label, cls: "text-[hsl(var(--hi-status-danger))]", note: "automazione da verificare (template non utilizzabile)" };
      case "unknown": return { icon: "✅", text: a.label, cls: "text-foreground", note: "configurata" };
      default: return { icon: "▫️", text: a.label, cls: "text-muted-foreground", note: "da configurare (Impostazioni avanzate)" };
    }
  };

  return (
    <div className="rounded-md border border-border p-3 space-y-2" data-testid="wa-automations-status">
      <div className="flex items-center justify-between">
        <Label className="text-sm flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Automazioni WhatsApp</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={load} disabled={loading} data-testid="wa-automations-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Aggiorna
        </Button>
      </div>
      {!data ? (
        <p className="text-[11px] text-muted-foreground py-1">Stato non disponibile.</p>
      ) : (
        <ul className="space-y-1">
          {data.automations.map((a) => { const v = view(a); return (
            <li key={a.event} className="flex items-center gap-2 text-sm" data-testid={`wa-auto-${a.event}`}>
              <span>{v.icon}</span>
              <span className={v.cls}>{v.text}</span>
              {v.note && <span className="text-[11px] text-muted-foreground">— {v.note}</span>}
            </li>
          ); })}
        </ul>
      )}
      {data && !data.waba_reachable && (
        <p className="text-[11px] text-muted-foreground pt-1">Stato template non verificabile ora (in Preview o credenziali WhatsApp assenti): le automazioni configurate restano operative in produzione.</p>
      )}
    </div>
  );
}

